import * as React from 'react';
import * as _ from 'underscore';

import { FormikTouched } from 'formik';
import { useCallback, useEffect } from 'react';
import { useSettingsCoordinatorStateInternal } from '~settings-coordinator/SettingsCoordinatorHook';
import { BaseReactSettingsCtrlProps } from '../../controllers/BaseReactSettingsCtrl';
import { Formik, FormikErrors, FormikHelpers, useFormikContext } from '../../FormikWrapperForStubbing';
import { SettingsComponentRegistry } from './SettingsComponentRegistry';
import { SettingsCoordinatorContext } from '~settings-coordinator/SettingsCoordinatorContexts';

export const InputDebounceMs = 200;

export interface SectionContainerBaseProps {
  sections: string[];
  settings: object;
}

function useWireFormikCallbacks(props: BaseReactSettingsCtrlProps) {
  // get formik specific functionality from the context. This is cleaner than polluting props with an HOC.
  const formik = useFormikContext<BaseReactSettingsCtrlProps>();
  const {
    dispatch,
    triggerSave,
    triggerRevert,
    isAnyDirty,
    isAnyInvalid,
    isAnySubmitting
  } = useSettingsCoordinatorStateInternal();

  // wrapper callback for submitForm because it has a different signature than what setSaveCallback expects
  const submitFormCallback = useCallback(async () => {
    await Promise.all([
      formik.submitForm(),
      triggerSave()
      ]);
  }, [formik.submitForm, triggerSave]);

  useEffect(() => {
    props.setSaveCallback(submitFormCallback);
    props.setCancelCallback(() => {
      formik.resetForm();
      formik.validateForm();
      triggerRevert();
    });
    props.setOpenNavigationDialogCallback(null);
    props.setIsBusyCallback(() => formik.isSubmitting || isAnySubmitting);
  }, [triggerRevert, formik?.resetForm, formik?.validateForm]);

  useEffect(() => {
    props.setIsFormDirty(formik.dirty || isAnyDirty);
    props.setIsFormValid(formik.isValid && !isAnyInvalid);
    props.setIsBusyCallback(() => formik.isSubmitting || isAnySubmitting);
  }, [formik.isSubmitting, formik.dirty, formik.isValid, isAnyDirty, isAnySubmitting, isAnyInvalid]);

  useEffect(() => {
    props.mergeIntoParentScope?.(formik.values);
  }, [formik.values]);

  return dispatch;
}

function handleSubmit(values: BaseReactSettingsCtrlProps, actions: FormikHelpers<BaseReactSettingsCtrlProps>) {
  actions.setSubmitting(false);
  actions.resetForm({values});
  actions.validateForm(); // this is needed to enable the buttons after data is changed externally from the angular world
}

function SettingsSectionList(props: SectionContainerBaseProps & BaseReactSettingsCtrlProps) {
  const dispatch = useWireFormikCallbacks(props);
  const {sections, ...rest} = props;

  const unregisteredSections = props.sections?.filter(key => !(key in SettingsComponentRegistry));

  if(unregisteredSections?.length > 0) {
    throw new Error(`Tried to render unregistered settings sections: ${unregisteredSections}`);
  }

  return <SettingsCoordinatorContext.Provider value={dispatch}>
    {props.sections?.map((key, index) => {
      const Section = SettingsComponentRegistry[key];
      return <Section.component key={index} {...rest}/>;
    })}
  </SettingsCoordinatorContext.Provider>;
}

export function SettingsFormikContainer<Props extends SectionContainerBaseProps>(props: SectionContainerBaseProps
  & BaseReactSettingsCtrlProps
  & {
    initErrors?: FormikErrors<Props>;
    initTouched?: FormikTouched<Props>;
    }) {
  // filter values down to migrated settings.
  const {sections, ...rest} = props;

  const initValues = {
    settings: props.settings,
    availability: (props as any).availability
  };

  return <Formik initialValues={ initValues as any} // typescript is being difficult
                 onSubmit={handleSubmit}
                 validateOnMount={true}
                 initialTouched={props.initTouched}
                 initialErrors={props.initErrors}>
      <SettingsSectionList {...props}/>
  </Formik>;
}
